import { useLazyQuery } from "@apollo/client";
import { clientEntities } from "@neonumy/api-clients";
import { LIST_CLIENTS } from "queries/entities/clients";
import { ListClientsInput, ListClientsOutput } from "queries/types/entities/clients";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { onErrorGraphql } from "utils/onErrorGraphql";

interface UseListClientsProps {
  pageSize: number;
  offset: number;
  filters: Omit<NonNullable<ListClientsInput["filter"]>, "companyId" | "entityType">;
}

export default function useListClients({ pageSize, offset, filters }: UseListClientsProps) {
  const { companyId } = useParams();
  const [orderBy, setOrderBy] = useState<string[] | undefined>(undefined);

  const [listClients, { loading, data, error }] = useLazyQuery<ListClientsOutput, ListClientsInput>(
    LIST_CLIENTS,
    {
      client: clientEntities,
    },
  );

  useEffect(() => {
    if (error) onErrorGraphql(error);
  }, [error]);

  const fetchClients = (orderByParam?: string[]) => {
    if (companyId) {
      listClients({
        variables: {
          filter: {
            companyId,
            entityType: "CLIENT",
            ...filters,
          },
          limit: pageSize,
          offset,
          orderBy: orderByParam,
        },
      });
    }
  };

  useEffect(() => {
    fetchClients(orderBy);
  }, [companyId, filters, pageSize, offset, orderBy]);

  const clientsList =
    data?.listEntities?.results.map(client => ({ key: client.id, ...client })) || [];
  const totalCount = data?.listEntities?.totalCount || 0;

  const handleSort = (columnKey: string, order: "ascend" | "descend" | undefined) => {
    if (!order) {
      setOrderBy(undefined);
      return;
    }
    const newOrderBy = order === "ascend" ? [columnKey] : [`-${columnKey}`];
    setOrderBy(newOrderBy);
  };

  return { fetchClients, loading, clientsList, totalCount, handleSort };
}
